import $ from 'jquery';
import { defer } from 'underscore';

const LIST_CLASS = 'a11y-adapter-list';
const ITEM_CLASS = 'a11y-adapter-item';
const FOCUS_CLASS = 'a11y-adapter-item-focused';
const SELECT_CLASS = 'a11y-adapter-item-selected';
const UP_KEY_CODE = 40;
const DOWN_KEY_CODE = 38;
const SPACE_KEY_CODE = 32;

export class DoubleListA11yHacker {
  constructor(options) {
    const {
      rootSelector = 'body',
      listViewSelector,
      itemSelector,
      interactableElementSelector,
      listAriaLabel,
    } = options;

    this.rootSelector = rootSelector;
    this.listViewSelector = listViewSelector;
    this.itemSelector = itemSelector;
    this.interactableElementSelector = interactableElementSelector;
    this.listAriaLabel = listAriaLabel;
    this.$lastFocusedElement = null;
    this.rowPositions = [];
    this.rowHeights = [];
    this.vh = 0;
    this.maxScrollTop = 0;
    this.$interactableElements = null;

    this._onListViewKeyDown = this._onListViewKeyDown.bind(this);
    this._onListViewClick = this._onListViewClick.bind(this);
    this._onListViewBlur = this._onListViewBlur.bind(this);
    this._onListViewFocus = this._onListViewFocus.bind(this);
  }

  refresh() {
    this.$lastFocusedElement = null;
    this.$root = $(this.rootSelector);
    this.vh = this.$root.innerHeight();
    this.$listView = $(this.listViewSelector);
    this.$listView.attr({
      role: 'listbox',
      'aria-label': this.listAriaLabel,
      tabindex: '0',
    }).addClass(LIST_CLASS);
    this.$rows = this.$listView.find(this.itemSelector);
    this.$rows.attr({
      role: 'option',
      'aria-selected': 'false',
    }).addClass(ITEM_CLASS);

    this.rowPositions = [];
    this.rowHeights = [];
    this.$rows.each((index) => {
      const $row = this.$rows.eq(index);

      this.rowPositions.push($row.position().top);
      this.rowHeights.push($row.outerHeight(true));
    });

    this.$interactableElements = this.$listView.find(this.itemSelector).find(this.interactableElementSelector);

    this.$interactableElements.attr({
      tabindex: '-1',
    });

    this._initializeEvents();
  }

  _initializeEvents() {
    this.$listView
      .off('keydown', this._onListViewKeyDown)
      .off('focus', this._onListViewFocus)
      .off('blur', this._onListViewBlur)
      .off('click', this.itemSelector, this._onListViewClick);

    this.$listView
      .on('focus', this._onListViewFocus)
      .on('blur', this._onListViewBlur)
      .on('keydown', this._onListViewKeyDown)
      .on('click', this.itemSelector, this._onListViewClick);
  }

  _onListViewFocus() {
    // make sure onclick handler executes first
    defer(() => {
      if (!this.$lastFocusedElement) {
        this.$lastFocusedElement = this.$rows.first();
      }
      this.focusRow(this.$lastFocusedElement);
    });
  }

  _onListViewBlur() {
    this.blurAll();
  }

  _onListViewClick(evt) {
    const { currentTarget, target } = evt;

    const isDescendantOfInteractableEle = this.$interactableElements.has(target).length > 0;
    const isInteractableEle = this.$interactableElements.index(target) !== -1;

    // check if the event is triggered inside a interactable element or is exactly an interactable element
    if (!isDescendantOfInteractableEle && !isInteractableEle) {
      this.focusRow($(currentTarget));
    }
  }

  _onListViewKeyDown(evt) {
    const { keyCode } = evt;

    if (this.$listView.is(':focus')) {
      const currentIndex = this.$lastFocusedElement ? this.$lastFocusedElement.index() : -1;

      if (keyCode === UP_KEY_CODE) {
        const newIndex = (currentIndex + 1) % this.$rows.length;

        evt.preventDefault();
        this.focusRowAtIndex(newIndex);
        this.scrollToCenter(newIndex);
      } else if (keyCode === DOWN_KEY_CODE) {
        const newIndex = (currentIndex - 1 + this.$rows.length) % this.$rows.length;

        evt.preventDefault();
        this.focusRowAtIndex(newIndex);
        this.scrollToCenter(newIndex);
      } else if (keyCode === SPACE_KEY_CODE) {
        evt.stopPropagation();
        evt.preventDefault();
        this.selectRowAtIndex(currentIndex);
      }
    }
  }

  focusRow($row) {
    this.deselectAll();
    this.blurAll();
    $row.addClass(FOCUS_CLASS);
    this.$lastFocusedElement = $row;
  }

  blurAll() {
    this.$rows.removeClass(FOCUS_CLASS);
  }

  focusRowAtIndex(index = 0) {
    const $row = this.$rows.eq(index);

    this.focusRow($row);
  }

  selectRow($row) {
    this.focusRow($row);
    this.deselectAll();
    const $interactableElementsInRow = $row.find(this.interactableElementSelector);

    $interactableElementsInRow.attr('tabindex', '0');
    $row.addClass(SELECT_CLASS).attr('aria-selected', 'true');
  }

  deselectAll() {
    this.$interactableElements
      .attr('tabindex', '-1');

    this.$rows.removeClass(SELECT_CLASS).attr('aria-selected', 'false');
  }

  selectRowAtIndex(index) {
    const $row = this.$rows.eq(index);

    this.selectRow($row);
  }

  scrollToCenter(rowIndex) {
    const rowPos = this.rowPositions[rowIndex];
    const rowHeight = this.rowHeights[rowIndex];

    if (rowPos != null) {
      let centerOffset = rowPos - (this.vh / 2) - (rowHeight / 2);

      if (centerOffset < 0) {
        centerOffset = 0;
      }

      this.$root.scrollTop(centerOffset);
    }
  }
}
